import React, {useMemo, useRef} from 'react';
import {Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme} from '@mui/material';
import {
    CloseOutlined,
    ControlCamera,
    DeleteOutline,
    MarkEmailReadOutlined,
} from '@mui/icons-material';
import Draggable from 'react-draggable';
import {useTranslation} from 'react-i18next';
import IconButton from '../../../../../components/base/button/icon_button';
import DateUtils from '../../../../../../core/services/date-utils';

const peekWidth = 480;
const peekHeight = 420;
const cascadeStep = 28;
const viewportInset = 24;
const fallbackOrigin = {x: 48, y: 88};
const dragHandleClass = 'contact-us-message-peek-drag';
const cancelClass = 'draggable-popover-cancel';

/**
 * One cell of the sender 2x2 table.
 * @param {{label: string, value?: string | number | null}} props
 */
const SenderCell = ({label, value}) => {
    const display = value == null || value === '' ? '—' : value;
    return (
        <Box sx={{minWidth: 0}}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{wordBreak: 'break-word', cursor: 'text', userSelect: 'text', mt: 0.25}}
            >
                {display}
            </Typography>
        </Box>
    );
};

/**
 * Formats a submit datetime for the peek chrome.
 */
const formatSubmitDateTime = (value) => {
    if (!value) {
        return '';
    }
    return DateUtils.serverToLocal(value).format(DateUtils.dateTimeFormat);
};

/**
 * Clamps a peek origin to the viewport after applying cascade offset.
 * @param {{x?: number, y?: number}|null|undefined} origin
 * @param {number} cascadeIndex
 * @return {{x: number, y: number}}
 */
const getDefaultPeekPosition = (origin, cascadeIndex) => {
    const maxX = Math.max(0, window.innerWidth - peekWidth - viewportInset);
    const maxY = Math.max(0, window.innerHeight - peekHeight - viewportInset);
    const baseX = Number.isFinite(origin?.x) ? origin.x : fallbackOrigin.x;
    const baseY = Number.isFinite(origin?.y) ? origin.y : fallbackOrigin.y;
    return {
        x: Math.min(Math.max(0, baseX + cascadeIndex * cascadeStep), maxX),
        y: Math.min(Math.max(0, baseY + cascadeIndex * cascadeStep), maxY),
    };
};

/**
 * Viewport-fixed draggable peek for a Contact Us message.
 * Visual chrome mirrors product-information / lookup applied-filters popovers.
 *
 * @param {{
 *   message: object,
 *   cascadeIndex: number,
 *   origin?: {x: number, y: number}|null,
 *   focused: boolean,
 *   loading?: boolean,
 *   onFocus?: () => void,
 *   onClose?: () => void,
 *   onMarkAsRead?: (id: string) => void,
 *   onRemove?: (message: object) => void,
 * }} props
 */
const MessagePeek = ({
    message,
    cascadeIndex = 0,
    origin = null,
    focused = false,
    loading = false,
    onFocus,
    onClose,
    onMarkAsRead,
    onRemove,
}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.support.contactUsMessages'});
    const theme = useTheme();
    const nodeRef = useRef(null);

    const defaultPosition = useMemo(() => (
        getDefaultPeekPosition(origin, cascadeIndex)
    ), [cascadeIndex, origin]);

    const baseZ = theme.zIndex.modal + 1;

    return (
        <Draggable
            nodeRef={nodeRef}
            handle={`.${dragHandleClass}`}
            cancel={`.${cancelClass}`}
            bounds="body"
            defaultPosition={defaultPosition}
            onStart={() => onFocus?.()}
        >
            <Paper
                ref={nodeRef}
                elevation={0}
                onMouseDown={() => onFocus?.()}
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: `min(90vw, ${peekWidth}px)`,
                    maxHeight: `min(90vh, ${peekHeight}px)`,
                    zIndex: focused ? baseZ + 50 : baseZ + cascadeIndex,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    pointerEvents: 'auto',
                    boxShadow: theme.shadows[6],
                    bgcolor: 'background.paper',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: 1,
                        maxHeight: `min(90vh, ${peekHeight}px)`,
                        overflowY: 'auto',
                        position: 'relative',
                        gap: 1.5,
                        p: 1,
                    }}
                >
                    {/* Sticky drag chrome — same pattern as product-information popover */}
                    <Box
                        className={dragHandleClass}
                        sx={{
                            position: 'sticky',
                            top: -8,
                            zIndex: 3,
                            left: 0,
                            bgcolor: 'background.paper',
                            p: 1,
                            pe: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            borderBottom: 1,
                            borderBottomColor: 'divider',
                            cursor: 'grab',
                            '&:active': {cursor: 'grabbing'},
                            userSelect: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 0.5,
                            }}
                        >
                            <Tooltip title={t('peek.moveHint')} placement="right" followCursor>
                                <ControlCamera color="primary"/>
                            </Tooltip>

                            <Box
                                className={cancelClass}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <Button
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    disabled={!!message.isView || loading}
                                    startIcon={<MarkEmailReadOutlined/>}
                                    onClick={() => onMarkAsRead?.(message.id)}
                                    sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}
                                >
                                    {t('actions.markAsRead')}
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    color="error"
                                    disabled={loading}
                                    startIcon={<DeleteOutline/>}
                                    onClick={() => onRemove?.(message)}
                                    sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}
                                >
                                    {t('actions.remove')}
                                </Button>
                                <IconButton
                                    color="error"
                                    size="medium"
                                    aria-label={t('actions.close')}
                                    onPressed={() => onClose?.()}
                                >
                                    <CloseOutlined/>
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        className={cancelClass}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            px: 1,
                            pb: 1.5,
                            minHeight: 120,
                            userSelect: 'text',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <TableContainer
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    tableLayout: 'fixed',
                                    width: 1,
                                    borderCollapse: 'collapse',
                                    '& td': {
                                        border: 0,
                                        px: 1.5,
                                        py: 1.25,
                                        verticalAlign: 'top',
                                        width: '50%',
                                    },
                                    '& td + td': {
                                        borderLeft: 1,
                                        borderLeftColor: 'divider',
                                    },
                                    '& tr:not(:last-of-type) td': {
                                        borderBottom: 1,
                                        borderBottomColor: 'divider',
                                    },
                                }}
                            >
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <SenderCell
                                                label={t('columns.name')}
                                                value={message.name}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <SenderCell
                                                label={t('columns.email')}
                                                value={message.email}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <SenderCell
                                                label={t('columns.phone')}
                                                value={message.phone}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <SenderCell
                                                label={t('columns.submitDateTime')}
                                                value={formatSubmitDateTime(message.submitDateTime)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{wordBreak: 'break-word', cursor: 'text', userSelect: 'text'}}
                        >
                            {message.subject || t('reader.noSubject')}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: 'text'}}
                        >
                            {message.message || t('emptyMessage')}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Draggable>
    );
};

export default MessagePeek;
